/**
 * DATA — Adaptador Supabase: playerRepository
 *
 * Operações de presença do Player: heartbeat, status de conexão e
 * leitura da tela. Leituras/escritas do caminho anon (buscarPorId,
 * listarMidias, listarItensPlaylist, marcarOffline, marcarErroEmbed)
 * passam por RPCs SECURITY DEFINER escopadas por tela_id —
 * telas/midias/playlist_itens não têm policy de RLS para anon.
 */
import { supabase }              from '@/shared/lib/supabase';
import { fetchAllRpcPages }      from '@/shared/lib/fetchAllRpcPages';
import { telasService }          from '@/shared/services/telasService';
import { configuracoesService }  from '@/shared/services/configuracoesService';
import { jukeboxService }        from '@/shared/services/jukeboxService';
import type { IPlayerRepository, ItemExibicao } from '../domain/ports/IPlayerRepository';
import type { Tela } from '../domain/entities/Tela';
import type { Midia } from '@/shared/models/midia.model';
import type { PlaylistItem } from '@/shared/models/playlist.model';
import type { CampanhaPlayerRow } from '../domain/usecases/campanhasPlayer';

export type { ItemExibicao };

export const playerRepository: IPlayerRepository = {

  async buscarPorId(telaId: string): Promise<Tela> {
    const { data, error } = await supabase.rpc('player_buscar_tela' as never, { p_tela_id: telaId } as never);
    if (error) throw error;
    const rows = (data ?? []) as unknown as Tela[];
    if (rows.length === 0) throw new Error(`Tela ${telaId} não encontrada`);
    return rows[0];
  },

  async atualizarHeartbeat(
    telaId: string,
    contexto?: { telaNome?: string | null; empresaId?: string | null },
  ): Promise<void> {
    return telasService.atualizarHeartbeat(telaId, contexto);
  },

  async marcarOffline(telaId: string): Promise<void> {
    const { error } = await supabase.rpc('player_marcar_offline' as never, { p_tela_id: telaId } as never);
    if (error) console.error('[Status] Erro ao marcar offline:', error.message);
  },

  async buscarConfiguracoes(telaId: string) {
    return configuracoesService.buscarParaPlayer(telaId);
  },

  configDefaults() {
    return configuracoesService.defaults();
  },

  async listarMidias(telaId: string): Promise<Midia[]> {
    const { data, error } = await supabase.rpc('player_listar_midias' as never, { p_tela_id: telaId } as never);
    if (error) throw error;
    return (data ?? []) as unknown as Midia[];
  },

  async listarCampanhas(telaId: string): Promise<{ reservaPct: number; campanhas: CampanhaPlayerRow[] } | null> {
    const { data, error } = await supabase.rpc('player_listar_campanhas' as never, { p_tela_id: telaId } as never);
    if (error) throw error;
    if (!data) return null;
    const resposta = data as unknown as { reserva_pct: number; campanhas: CampanhaPlayerRow[] };
    return { reservaPct: resposta.reserva_pct, campanhas: resposta.campanhas ?? [] };
  },

  async registrarExibicoes(telaId: string, loteId: string, itens: ItemExibicao[]): Promise<void> {
    const { error } = await supabase.rpc('registrar_exibicoes_anuncio' as never, {
      p_tela_id: telaId,
      p_lote_id: loteId,
      p_itens: itens,
    } as never);
    if (error) throw error;
  },

  async listarItensPlaylist(playlistId: string): Promise<PlaylistItem[]> {
    return fetchAllRpcPages<PlaylistItem>('player_listar_playlist_itens', { p_playlist_id: playlistId });
  },

  listarAtivosJukebox(telaId: string) {
    return jukeboxService.listarAtivos(telaId);
  },

  atualizarStatusJukebox(id: string, status: 'pendente' | 'tocando' | 'concluido', telaId: string) {
    return jukeboxService.atualizarStatus(id, status, telaId);
  },

  resolverTocandoDuplicados(telaId: string) {
    return jukeboxService.resolverTocandoDuplicados(telaId);
  },

  async marcarErroEmbed(telaId: string, itemId: string): Promise<void> {
    const { error } = await supabase.rpc(
      'player_marcar_erro_embed' as never,
      { p_tela_id: telaId, p_item_id: itemId } as never,
    );
    if (error) throw error;
  },
};
